// TODO Type 'T' does not satisfy the constraint 'DateValue'.
// @ts-nocheck
import {
  Calendar,
  CalendarDate,
  CalendarDateTime,
  DateDuration,
  DateValue,
  GregorianCalendar,
  ZonedDateTime,
  isEqualDay,
  maxDate,
  minDate,
  startOfMonth,
  startOfWeek,
  startOfYear,
  toCalendar,
  toCalendarDate,
  createCalendar as cCalendar,
  CalendarIdentifier,
} from '@internationalized/date';
import { useControlledState } from '@react-stately/utils';
import {
  CSSProperties,
  ReactElement,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  RangeCalendarProps,
  VisuallyHidden,
  useLocale,
  useRangeCalendar,
  AriaRangeCalendarProps,
} from 'react-aria';
import {
  ButtonContext,
  HeadingContext,
  useContextProps,
} from 'react-aria-components';
import { RangeCalendarContext } from 'react-aria-components';
import { Provider } from 'react-aria-components';
import { RangeCalendarStateContext } from 'react-aria-components';
import { TextContext } from 'react-aria-components';
import {
  CalendarState,
  RangeCalendarState,
  ValidationState,
  useCalendarState,
} from 'react-stately';

export interface RangeValue<T> {
  /** The start value of the range. */
  start: T;
  /** The end value of the range. */
  end: T;
}

export const constrainValue = (
  date: CalendarDate,
  minValue?: DateValue | null,
  maxValue?: DateValue | null,
): CalendarDate => {
  if (minValue) {
    const newDate = maxDate(date, toCalendarDate(minValue));
    if (newDate) {
      date = newDate;
    }
  }

  if (maxValue) {
    const newDate = minDate(date, toCalendarDate(maxValue));
    if (newDate) {
      date = newDate;
    }
  }

  return date;
};

export const previousAvailableDate = (
  date: CalendarDate,
  minValue: DateValue,
  isDateUnavailable?: (date: CalendarDate) => boolean,
): CalendarDate | null => {
  if (!isDateUnavailable) {
    return date;
  }

  while (date.compare(minValue) >= 0 && isDateUnavailable(date)) {
    date = date.subtract({ days: 1 });
  }

  if (date.compare(minValue) >= 0) {
    return date;
  }
  return null;
};

export const isInvalid = (
  date: DateValue,
  minValue?: CalendarDate | CalendarDateTime | ZonedDateTime | null,
  maxValue?: CalendarDate | CalendarDateTime | ZonedDateTime | null,
): boolean => {
  return (
    (minValue !== null &&
      minValue !== undefined &&
      date.compare(minValue) < 0) ||
    (maxValue !== null && maxValue !== undefined && date.compare(maxValue) > 0)
  );
};

export const alignStart = (
  date: CalendarDate,
  duration: DateDuration,
  locale: string,
  minValue?: DateValue | null,
  maxValue?: DateValue | null,
): CalendarDate => {
  // align to the start of the largest unit
  let aligned = date;
  if (duration.years) {
    aligned = startOfYear(date);
  } else if (duration.months) {
    aligned = startOfMonth(date);
  } else if (duration.weeks) {
    aligned = startOfWeek(date, locale);
  }

  return constrainStart(date, aligned, duration, locale, minValue, maxValue);
};

export const alignCenter = (
  date: CalendarDate,
  duration: DateDuration,
  locale: string,
  minValue?: DateValue | null,
  maxValue?: DateValue | null,
): CalendarDate => {
  const halfDuration = {} as DateDuration;
  for (const key in duration) {
    const typedKey = key as keyof DateDuration;
    halfDuration[typedKey] = duration[typedKey]
      ? Math.floor((duration[typedKey] ?? 0) / 2)
      : 0;
    if (
      (halfDuration[typedKey] ?? 0) > 0 &&
      (duration[typedKey] ?? 0) % 2 === 0
    ) {
      halfDuration[typedKey] = (halfDuration[typedKey] ?? 0) - 1;
    }
  }
  const aligned = alignStart(date, duration, locale).subtract(halfDuration);
  return constrainStart(date, aligned, duration, locale, minValue, maxValue);
};

export const alignEnd = (
  date: CalendarDate,
  duration: DateDuration,
  locale: string,
  minValue?: DateValue | null,
  maxValue?: DateValue | null,
): CalendarDate => {
  const d = { ...duration };
  // subtract 1 from the smallest unit
  if (d.days) {
    d.days--;
  } else if (d.weeks) {
    d.weeks--;
  } else if (d.months) {
    d.months--;
  } else if (d.years) {
    d.years--;
  }

  const aligned = alignStart(date, duration, locale).subtract(d);
  return constrainStart(date, aligned, duration, locale, minValue, maxValue);
};

export const constrainStart = (
  date: CalendarDate,
  aligned: CalendarDate,
  duration: DateDuration,
  locale: string,
  minValue?: DateValue | null,
  maxValue?: DateValue | null,
): CalendarDate => {
  if (minValue && date.compare(minValue) >= 0) {
    const newDate = maxDate(
      aligned,
      alignStart(toCalendarDate(minValue), duration, locale),
    );
    if (newDate) {
      aligned = newDate;
    }
  }

  if (maxValue && date.compare(maxValue) <= 0) {
    const newDate = minDate(
      aligned,
      alignEnd(toCalendarDate(maxValue), duration, locale),
    );
    if (newDate) {
      aligned = newDate;
    }
  }

  return aligned;
};
export interface RangeCalendarStateOptions<T extends DateValue = DateValue>
  extends RangeCalendarProps<T> {
  /** The locale to display and edit the value according to. */
  locale: string;
  /**
   * A function that creates a [Calendar](../internationalized/date/Calendar.html)
   * object for a given calendar identifier. Such a function may be imported from the
   * `@internationalized/date` package, or manually implemented to include support for
   * only certain calendars.
   */
  createCalendar: (name: CalendarIdentifier) => Calendar;
  /**
   * The amount of days that will be displayed at once. This affects how pagination works.
   */
  visibleDuration?: DateDuration;
}

export function useRangeCalendarState<T extends DateValue = DateValue>(
  props: RangeCalendarStateOptions<T>,
): RangeCalendarState {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    createCalendar,
    locale,
    visibleDuration = { months: 1 },
    minValue,
    maxValue,
    ...calendarProps
  } = props;
  const [value, setValue] = useControlledState<RangeValue<T> | null>(
    valueProp,
    defaultValue ?? null,
    onChange as ((v: RangeValue<T> | null) => void) | undefined,
  );

  const [anchorDate, setAnchorDateState] = useState<CalendarDate | null>(null);
  let alignment: 'center' | 'start' = 'center';
  if (value && value.start && value.end) {
    const start = alignCenter(
      toCalendarDate(value.start),
      visibleDuration,
      locale,
      minValue,
      maxValue,
    );
    const end = start.add(visibleDuration).subtract({ days: 1 });

    if (value.end.compare(end) > 0) {
      alignment = 'start';
    }
  }

  // Available range must be stored in a ref so we have access to the updated version immediately in `isInvalid`.
  const availableRangeRef = useRef<Partial<RangeValue<DateValue>> | null>(null);
  const [availableRange, setAvailableRange] = useState<Partial<
    RangeValue<DateValue>
  > | null>(null);
  const min = useMemo(
    () => maxDate(minValue, availableRange?.start),
    [minValue, availableRange],
  );
  const max = useMemo(
    () => minDate(maxValue, availableRange?.end),
    [maxValue, availableRange],
  );

  const calendar = useCalendarState({
    ...calendarProps,
    value: value && value.start,
    createCalendar,
    locale,
    visibleDuration,
    minValue: min,
    maxValue: max,
    selectionAlignment: alignment,
  });

  const updateAvailableRange = useCallback(
    (date: CalendarDate | null) => {
      if (date && props.isDateUnavailable && !props.allowsNonContiguousRanges) {
        const nextAvailableStartDate = nextUnavailableDate(date, calendar, -1);
        const nextAvailableEndDate = nextUnavailableDate(date, calendar, 1);
        availableRangeRef.current = {
          start: nextAvailableStartDate,
          end: nextAvailableEndDate,
        };
        setAvailableRange(availableRangeRef.current);
      } else {
        availableRangeRef.current = null;
        setAvailableRange(null);
      }
    },
    [calendar, props.allowsNonContiguousRanges, props.isDateUnavailable],
  );

  // If the visible range changes, we need to update the available range.
  const [lastVisibleRange, setLastVisibleRange] = useState(
    calendar.visibleRange,
  );
  if (
    !isEqualDay(calendar.visibleRange.start, lastVisibleRange.start) ||
    !isEqualDay(calendar.visibleRange.end, lastVisibleRange.end)
  ) {
    if (anchorDate) {
      updateAvailableRange(anchorDate);
    }
    setLastVisibleRange(calendar.visibleRange);
  }

  const setAnchorDate = useCallback(
    (date: CalendarDate | null) => {
      if (date) {
        setAnchorDateState(date);
        updateAvailableRange(date);
      } else {
        setAnchorDateState(null);
        updateAvailableRange(null);
      }
    },
    [updateAvailableRange],
  );

  const highlightedRange = anchorDate
    ? makeRange(anchorDate, calendar.focusedDate)
    : value && makeRange(value.start, value.end);

  const [isDragging, setDragging] = useState(false);

  const { isDateUnavailable } = props;
  const isInvalidSelection = useMemo(() => {
    if (!value || anchorDate) {
      return false;
    }

    if (
      isDateUnavailable &&
      (isDateUnavailable(value.start) || isDateUnavailable(value.end))
    ) {
      return true;
    }

    return (
      isInvalid(value.start, minValue, maxValue) ||
      isInvalid(value.end, minValue, maxValue)
    );
  }, [isDateUnavailable, value, anchorDate, minValue, maxValue]);

  const isValueInvalid = props.isInvalid || isInvalidSelection;
  const validationState: ValidationState | null = isValueInvalid
    ? 'invalid'
    : null;

  const selectDate = (date: CalendarDate) => {
    if (props.isReadOnly) {
      return;
    }

    const constrainedDate = constrainValue(date, min, max);
    const previousAvailableConstrainedDate = previousAvailableDate(
      constrainedDate,
      calendar.visibleRange.start,
      props.isDateUnavailable,
    );
    if (!previousAvailableConstrainedDate) {
      return;
    }
    if (!anchorDate) {
      setAnchorDate(previousAvailableConstrainedDate);
    } else {
      const range = makeRange(anchorDate, previousAvailableConstrainedDate);
      if (range) {
        setValue({
          start: convertValue(range.start, value?.start) as T,
          end: convertValue(range.end, value?.end) as T,
        });
      }
      setAnchorDate(null);
    }
  };
  useEffect(() => {
    if (anchorDate) {
      const range = makeRange(anchorDate, anchorDate.add({ days: 7 }));
      if (range) {
        setValue({
          start: convertValue(range.start, range.start) as T,
          end: convertValue(range.end, range.end) as T,
        });
      }
      setAnchorDate(null);
    }
  }, [anchorDate, setAnchorDate, setValue]);

  return {
    ...calendar,
    value,
    setValue: setValue as (value: RangeValue<T> | null) => void,
    anchorDate,
    setAnchorDate,
    highlightedRange,
    validationState,
    isValueInvalid,
    selectFocusedDate() {
      selectDate(calendar.focusedDate);
    },
    selectDate,
    highlightDate(date) {
      if (anchorDate) {
        calendar.setFocusedDate(date);
      }
    },
    isSelected(date) {
      return Boolean(
        highlightedRange &&
          date.compare(highlightedRange.start) >= 0 &&
          date.compare(highlightedRange.end) <= 0 &&
          !calendar.isCellDisabled(date) &&
          !calendar.isCellUnavailable(date),
      );
    },
    isInvalid(date) {
      return (
        calendar.isInvalid(date) ||
        isInvalid(
          date,
          availableRangeRef.current?.start,
          availableRangeRef.current?.end,
        )
      );
    },
    isDragging,
    setDragging,
  };
}

function makeRange(
  start: DateValue,
  end: DateValue,
): RangeValue<CalendarDate> | null {
  if (!start || !end) {
    return null;
  }

  if (end.compare(start) < 0) {
    [start, end] = [end, start];
  }

  return { start: toCalendarDate(start), end: toCalendarDate(end) };
}

function convertValue(newValue: CalendarDate, oldValue?: DateValue): DateValue {
  // The display calendar should not have any effect on the emitted value.
  // Emit dates in the same calendar as the original value, if any, otherwise gregorian.
  newValue = toCalendar(
    newValue,
    oldValue?.calendar ?? new GregorianCalendar(),
  );

  // Preserve time if the input value had one.
  if (oldValue && 'hour' in oldValue) {
    return oldValue.set(newValue);
  }

  return newValue;
}

function nextUnavailableDate(
  anchorDate: CalendarDate,
  state: CalendarState,
  dir: number,
): CalendarDate | undefined {
  let nextDate = anchorDate.add({ days: dir });
  while (
    (dir < 0
      ? nextDate.compare(state.visibleRange.start) >= 0
      : nextDate.compare(state.visibleRange.end) <= 0) &&
    !state.isCellUnavailable(nextDate)
  ) {
    nextDate = nextDate.add({ days: dir });
  }

  if (state.isCellUnavailable(nextDate)) {
    return nextDate.add({ days: -dir });
  }
}
export interface StyleRenderProps<T> {
  /** The CSS [className](https://developer.mozilla.org/en-US/docs/Web/API/Element/className) for the element. A function may be provided to compute the class based on component state. */
  className?:
    | string
    | ((values: T & { defaultClassName: string | undefined }) => string);
  /** The inline [style](https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/style) for the element. A function may be provided to compute the style based on component state. */
  style?:
    | CSSProperties
    | ((
        values: T & { defaultStyle: CSSProperties },
      ) => CSSProperties | undefined);
}

export interface RenderProps<T> extends StyleRenderProps<T> {
  /** The children of the component. A function may be provided to alter the children based on component state. */
  children?:
    | ReactElement
    | ((
        values: T & { defaultChildren: ReactElement | undefined },
      ) => ReactElement);
}

interface RenderPropsHookOptions<T> {
  className?: string | ((values: T & { defaultClassName?: string }) => string);
  style?:
    | CSSProperties
    | ((values: T & { defaultStyle?: CSSProperties }) => CSSProperties);
  children?:
    | ReactElement
    | ((values: T & { defaultChildren?: ReactElement }) => ReactElement);
  defaultClassName?: string;
  defaultChildren?: ReactElement;
  defaultStyle?: CSSProperties;
  values: T;
}

interface RenderPropsResult {
  className?: string;
  style?: CSSProperties;
  children?: ReactElement;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'data-rac': string;
}

export const useRenderProps = <T,>(
  props: RenderPropsHookOptions<T>,
): RenderPropsResult => {
  const {
    className,
    style,
    children,
    defaultClassName = undefined,
    defaultChildren = undefined,
    defaultStyle,
    values,
  } = props;

  return useMemo<RenderPropsResult>(() => {
    let computedClassName: string | undefined;
    let computedStyle: React.CSSProperties | undefined;
    let computedChildren: React.ReactNode | undefined;

    if (typeof className === 'function') {
      computedClassName = className({ ...values, defaultClassName });
    } else {
      computedClassName = className;
    }

    if (typeof style === 'function') {
      computedStyle = style({ ...values, defaultStyle: defaultStyle ?? {} });
    } else {
      computedStyle = style;
    }

    if (typeof children === 'function') {
      computedChildren = children({ ...values, defaultChildren });
    } else if (children === null) {
      computedChildren = defaultChildren;
    } else {
      computedChildren = children;
    }

    return {
      className: computedClassName ?? defaultClassName,
      style:
        computedStyle || defaultStyle
          ? { ...defaultStyle, ...computedStyle }
          : undefined,
      children: computedChildren ?? defaultChildren,
      'data-rac': '',
    };
  }, [
    className,
    style,
    children,
    defaultClassName,
    defaultChildren,
    defaultStyle,
    values,
  ]);
};

export const RangeCalendarWrapper = forwardRef<
  HTMLDivElement,
  AriaRangeCalendarProps<DateValue> & {
    slot?: string | null;
    children?: ReactElement;
    className?: string;
    ref?: Ref<HTMLDivElement>;
  }
>(({ ...props }, ref): ReactElement => {
  [props, ref] = useContextProps(props, ref, RangeCalendarContext);

  const { locale } = useLocale();
  const state = useRangeCalendarState({
    ...props,
    locale,
    createCalendar: cCalendar,
    value: props.value as RangeValue<DateValue> | null | undefined,
    defaultValue: props.defaultValue as
      | RangeValue<DateValue>
      | null
      | undefined,
    onChange: props.onChange as
      | ((value: RangeValue<DateValue> | null) => void)
      | undefined,
  });

  const {
    calendarProps,
    prevButtonProps,
    nextButtonProps,
    errorMessageProps,
    title,
  } = useRangeCalendar(props, state, ref);

  const renderProps = useRenderProps({
    ...props,
    values: {
      state,
      isDisabled: props.isDisabled || false,
      isInvalid: state.isValueInvalid,
    },
    defaultClassName: 'react-aria-RangeCalendar',
  });

  return (
    <div
      {...renderProps}
      {...calendarProps}
      ref={ref}
      slot={props.slot || undefined}
      data-disabled={props.isDisabled || undefined}
      data-invalid={state.isValueInvalid || undefined}
    >
      <Provider
        values={[
          [
            ButtonContext,
            {
              slots: {
                previous: prevButtonProps,
                next: nextButtonProps,
              },
            },
          ],
          [HeadingContext, { 'aria-hidden': true, level: 2, children: title }],
          [RangeCalendarStateContext, state],
          [
            TextContext,
            {
              slots: {
                errorMessage: errorMessageProps,
              },
            },
          ],
        ]}
      >
        <VisuallyHidden>
          <h2>{calendarProps['aria-label']}</h2>
        </VisuallyHidden>
        {renderProps.children}
        <VisuallyHidden>
          <button
            aria-label={nextButtonProps['aria-label']}
            disabled={nextButtonProps.isDisabled}
            onClick={() => {
              state.focusNextPage();
            }}
            tabIndex={-1}
          />
        </VisuallyHidden>
      </Provider>
    </div>
  );
});

RangeCalendarWrapper.displayName = 'RangeCalendarWrapper';
